import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { userLogsTableColumns } from "./user-logs-helpers";
import LaunchIcon from "@mui/icons-material/Launch";
import { TableBox, TableText } from "@shared/components/Table";
import { format } from "date-fns";
import styled from "@emotion/styled";

interface BasicUserThread {
  id: string;
  title: string;
  createdAt: string;
  deletedAt?: string;
}

type Props = {
  threads: BasicUserThread[];
  isLoadingThreads: boolean;
  handleThreadLogsClick: (id: string) => void;
};

const CustomTableCell = styled(TableCell)`
  &.item-deleted {
    font-style: italic;
    opacity: 0.6;
  }
`;

export const UserThreads = (props: Props) => {
  const { threads, isLoadingThreads, handleThreadLogsClick } = props;

  return (
    <TableBox>
      <TableContainer>
        <Table aria-label="table">
          <TableHead>
            <TableRow>
              {userLogsTableColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingThreads &&
              threads.length === 0 &&
              Array(10)
                .fill(null)
                .map((_, index) => (
                  <TableRow hover tabIndex={-1} key={index}>
                    {userLogsTableColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          width: column.width,
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={40}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

            {!isLoadingThreads && threads.length === 0 && (
              <TableRow hover>
                <TableCell colSpan={userLogsTableColumns.length} align="center">
                  <Typography variant="body2">
                    This user has no thread created
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!isLoadingThreads &&
              threads.map((thread) => {
                return (
                  <TableRow hover tabIndex={-1} key={thread.id}>
                    <CustomTableCell
                      className={thread.deletedAt ? "item-deleted" : undefined}
                    >
                      <TableText variant="body2">{thread.id}</TableText>
                      <TableText>{thread.title}</TableText>
                    </CustomTableCell>

                    <CustomTableCell
                      className={thread.deletedAt ? "item-deleted" : undefined}
                    >
                      <TableText variant="body2">
                        {format(
                          new Date(thread.createdAt),
                          "MM/dd/yyyy hh:mm:ss a"
                        )}
                      </TableText>
                    </CustomTableCell>

                    <CustomTableCell
                      className={thread.deletedAt ? "item-deleted" : undefined}
                    >
                      {thread.deletedAt ? (
                        <Tooltip
                          title={`Deleted on ${format(
                            new Date(thread.deletedAt),
                            "MM/dd/yyyy hh:mm:ss a"
                          )}`}
                          key={thread.id}
                        >
                          <TableText variant="body2">Deleted</TableText>
                        </Tooltip>
                      ) : (
                        <TableText variant="body2">Active</TableText>
                      )}
                    </CustomTableCell>

                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleThreadLogsClick(thread.id)}
                        type="button"
                      >
                        <LaunchIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </TableBox>
  );
};
